import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import {
  LayoutDashboard, ArrowLeftRight, PiggyBank, Target, CalendarClock,
  FolderTree, BarChart3, Settings, Plus, Upload, Download, Sun, Moon,
  LogOut, FileText, UserCircle, Users as UsersIcon, Lock, Bot, TrendingUp,
  ShieldCheck, GitBranch,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { downloadPdfReport, downloadExport } from '../lib/api';
import { toast } from 'sonner';

/**
 * Spotlight-style command palette. Triggered by ⌘K / Ctrl+K from anywhere.
 * Mounted once in Layout.
 */
const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { logout, user } = useAuth();

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const run = (fn) => () => {
    setOpen(false);
    setTimeout(fn, 50);
  };

  const goto = (path) => run(() => navigate(path));

  const exportNow = (format) =>
    run(async () => {
      try {
        const now = new Date();
        const blob = await downloadExport(format, now.getMonth() + 1, now.getFullYear());
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transazioni-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Export pronto');
      } catch {
        toast.error('Errore export');
      }
    });

  const downloadPdfNow = () =>
    run(async () => {
      try {
        const now = new Date();
        const blob = await downloadPdfReport('monthly', now.getMonth() + 1, now.getFullYear());
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report-${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Report pronto');
      } catch {
        toast.error('Errore generazione PDF');
      }
    });

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Cerca o esegui un'azione… (Esc per chiudere)" data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>Nessun risultato.</CommandEmpty>
        <CommandGroup heading="Naviga">
          <CommandItem onSelect={goto('/')}><LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard</CommandItem>
          <CommandItem onSelect={goto('/transactions')}><ArrowLeftRight className="mr-2 h-4 w-4" /> Transazioni</CommandItem>
          <CommandItem onSelect={goto('/budget')}><PiggyBank className="mr-2 h-4 w-4" /> Budget</CommandItem>
          <CommandItem onSelect={goto('/goals')}><Target className="mr-2 h-4 w-4" /> Obiettivi</CommandItem>
          <CommandItem onSelect={goto('/recurring')}><CalendarClock className="mr-2 h-4 w-4" /> Fatture ricorrenti</CommandItem>
          <CommandItem onSelect={goto('/categories')}><FolderTree className="mr-2 h-4 w-4" /> Categorie</CommandItem>
          <CommandItem onSelect={goto('/annual')}><BarChart3 className="mr-2 h-4 w-4" /> Report annuale</CommandItem>
          <CommandItem onSelect={run(() => {
            const n = new Date();
            navigate(`/monthly/${n.getFullYear()}/${n.getMonth() + 1}`);
          })}>
            <BarChart3 className="mr-2 h-4 w-4" /> Dettaglio mese corrente
          </CommandItem>
          <CommandItem onSelect={goto('/import')}><Upload className="mr-2 h-4 w-4" /> Importa da Excel</CommandItem>
          <CommandItem onSelect={goto('/family')}><UsersIcon className="mr-2 h-4 w-4" /> La mia famiglia</CommandItem>
          <CommandItem onSelect={goto('/by-member')}><UsersIcon className="mr-2 h-4 w-4" /> Movimenti per persona</CommandItem>
          <CommandItem onSelect={goto('/me')}><UserCircle className="mr-2 h-4 w-4" /> Area personale</CommandItem>
          <CommandItem onSelect={goto('/settings')}><Settings className="mr-2 h-4 w-4" /> Impostazioni</CommandItem>
        </CommandGroup>
        {user?.role === 'admin' && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Admin">
              <CommandItem onSelect={goto('/admin')}><ShieldCheck className="mr-2 h-4 w-4" /> Admin dashboard</CommandItem>
              <CommandItem onSelect={goto('/admin/families')}><UsersIcon className="mr-2 h-4 w-4" /> Famiglie</CommandItem>
              <CommandItem onSelect={goto('/admin/ai-providers')}><Bot className="mr-2 h-4 w-4" /> Provider AI</CommandItem>
              <CommandItem onSelect={goto('/admin/market-providers')}><TrendingUp className="mr-2 h-4 w-4" /> Provider market</CommandItem>
              <CommandItem onSelect={goto('/admin/quant')}><GitBranch className="mr-2 h-4 w-4" /> Quant Lab</CommandItem>
            </CommandGroup>
          </>
        )}
        <CommandSeparator />
        <CommandGroup heading="Azioni">
          <CommandItem onSelect={goto('/transactions?add=true')}><Plus className="mr-2 h-4 w-4" /> Nuova transazione</CommandItem>
          <CommandItem onSelect={goto('/transactions?add=true&private=true')}>
            <Lock className="mr-2 h-4 w-4" /> Nuova transazione privata
          </CommandItem>
          <CommandItem onSelect={downloadPdfNow}><FileText className="mr-2 h-4 w-4" /> Esporta PDF mese corrente</CommandItem>
          <CommandItem onSelect={exportNow('csv')}><Download className="mr-2 h-4 w-4" /> Esporta CSV mese corrente</CommandItem>
          <CommandItem onSelect={exportNow('xlsx')}><Download className="mr-2 h-4 w-4" /> Esporta Excel mese corrente</CommandItem>
          <CommandItem onSelect={run(toggleTheme)}>
            {theme === 'light' ? <Moon className="mr-2 h-4 w-4" /> : <Sun className="mr-2 h-4 w-4" />}
            Cambia tema
          </CommandItem>
          <CommandItem onSelect={run(logout)} className="text-destructive">
            <LogOut className="mr-2 h-4 w-4" /> Esci
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
